import React, { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import type { GridColDef } from "@mui/x-data-grid";
import { Alert } from "@components/Alert/Alert";
import LoadingOverlay from "@components/LoadingOverlay/LoadingOverlay";
import { Pagination } from "@components/Pagination/Pagination";
import { useTranslation } from "react-i18next";
import OperationDataGrid from "../components/OperationDataGrid";
import { DRYBULK_CARGO_OPS_LANG_BASE, OPERATION_LOG_PAGE_SIZE } from "../constants";
import { useOperationLog } from "../hooks/useOperationLog";
import { formatInstant } from "../utils/formatInstant";
import { operationLogActionText, operationLogActorText } from "../utils/operationLogText";

const L = `${DRYBULK_CARGO_OPS_LANG_BASE}.detail.operation-log`;

const OperationLogTab: React.FC<{ operationId: string; status: string | null | undefined }> = ({
  operationId,
  status,
}) => {
  const { t } = useTranslation();
  const { log, isLoading, isError, page, changePage } = useOperationLog(operationId, status);

  const columns = useMemo<GridColDef[]>(
    () => [
      { field: "action", headerName: t(`${L}.columns.action`), flex: 2, minWidth: 240 },
      { field: "createdBy", headerName: t(`${L}.columns.created-by`), flex: 1, minWidth: 160 },
      { field: "date", headerName: t(`${L}.columns.date`), flex: 1, minWidth: 140 },
    ],
    [t],
  );

  const rows = (log?.records ?? []).map((entry) => ({
    id: entry.id,
    action: operationLogActionText(t, entry),
    createdBy: operationLogActorText(t, entry),
    date: formatInstant(entry.createdAt),
  }));

  return (
    <Box
      data-testid="operation-log-tab"
      sx={{ display: "flex", flexDirection: "column", gap: 2, position: "relative" }}
    >
      <LoadingOverlay loading={isLoading} testId="operation-log-loading" />
      {log?.filtered === true && (
        <Box data-testid="operation-log-filtered-label">
          <Alert type="info">{t(`${L}.filtered-label`)}</Alert>
        </Box>
      )}
      {isError && (
        <Typography data-testid="operation-log-unavailable" color="text.secondary">
          {t(`${L}.unavailable`)}
        </Typography>
      )}
      {log && (
        <OperationDataGrid
          title={t(`${L}.caption`)}
          hideTitle
          testId="operation-log-grid"
          columns={columns}
          rows={rows}
          emptyMessage={t(`${L}.empty`)}
        />
      )}
      {log && log.totalRecordCount > OPERATION_LOG_PAGE_SIZE && (
        <Box data-testid="operation-log-pagination">
          <Pagination
            page={page}
            pageSize={OPERATION_LOG_PAGE_SIZE}
            totalCount={log.totalRecordCount}
            onPageChange={changePage}
            onPageSizeChange={() => undefined}
            showPageSizeOptions={false}
          />
        </Box>
      )}
    </Box>
  );
};

export default OperationLogTab;
